import { BaseApi } from "../base.js";
import { API } from "../constants.js";
import router from "@/router";
import { t } from "@/locales/i18n";

export class StakingApi extends BaseApi {
  getStakingBalances() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/staking/totals`),
      (d) => this.staking.setBalance(d.overall_totals),
    );
  }

  async getStakingItem(id) {
    const res = await this.request(
      () => this.r.get(`${API.ENDPOINT}/staking/${id}`),
      (d) => this.staking.setCurrentStaking(d.deposit),
    );

    if (res?.ok && res.data?.deposit?.status === "pending") {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return this.getStakingItem(id);
    }

    return res;
  }

  getStakingList() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/staking/active`),
      (d) => {
        this.staking.setList(d.deposits);
        if (d.deposits.length > 0 && d.deposits[0].id) {
          this.getStakingItem(d.deposits[0].id);
        } else {
          this.staking.setCurrentStaking({});
        }
        return Promise.resolve({ ok: true });
      },
    );
  }

  getStakingSettings(options = {}) {
    if (this.staking.settings.length) return Promise.resolve({ ok: true });
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/staking/settings/actual`, {}, options),
      (d) => this.staking.setSettings(d),
    );
  }

  setCurrentStaking(setting) {
    return this.staking.setCurrentStaking(setting);
  }

  openStaking() {
    const f = this.staking.form;
    const body = {
      amount: Number(f.amount) || 0,
      setting_id: f.setting.id,
    };
    return this.request(
      () => this.r.post(`${API.ENDPOINT}/staking/open`, body),
      (d) => {
        this.getStakingItem(d.deposit_id);
        return router.replace({
          name: "StakingItem",
          params: { id: d.deposit_id },
        });
      },
    );
  }

  topUpStaking(value) {
    const id = this.staking.currentStaking.id;
    return this.request(
      () =>
        this.r.post(`${API.ENDPOINT}/staking/topup`, {
          deposit_id: id,
          amount: Number(value),
        }),
      () => {
        setTimeout(() => {
          this.getStakingItem(id);
        }, 1000);
        router.replace({
          name: "StakingSuccess",
          query: {
            title: t("staking|stakingHasBeenSuccessfullyFunded"),
            button: t("staking|goBack"),
          },
        });
      },
    );
  }

  withdrawStaking(value) {
    const id = this.staking.currentStaking.id;
    return this.request(
      () =>
        this.r.post(`${API.ENDPOINT}/staking/withdraw`, {
          deposit_id: id,
          amount: Number(value),
        }),
      () => {
        setTimeout(() => {
          this.getStakingItem(id);
        }, 1000);
        router.replace({
          name: "StakingSuccess",
          query: {
            title: t("staking|fundsSuccessfullyWithdrawn"),
            button: t("staking|goBack"),
          },
        });
      },
    );
  }

  closeStaking() {
    const id = this.staking.currentStaking.id;
    return this.request(
      () => this.r.post(`${API.ENDPOINT}/staking/close`, { deposit_id: id }),
      () =>
        this.getStakingList()
          .then(() =>
            router.replace({
              name: "StakingSuccess",
              query: {
                title: t("staking|stakingIsClosed"),
                button: t("staking|goBack"),
                path: "/staking",
              },
            }),
          )
          .then(() => this.getStakingList()),
    );
  }

  getDepositHistory(value, options = {}) {
    const id = this.staking.currentStaking.id;
    const { ...query } = value;

    const queryParams = Object.keys(query)
      .filter((key) => query[key] != null && query[key] !== "")
      .map((key) => `${key}=${query[key]}`)
      .join("&");

    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/staking/${id}/operations?${queryParams}`,
          undefined,
          options,
        ),
      (d) => this.staking.setHistory(d, { append: value.offset > 0 }),
    );
  }

  getStakingHistory(value, options = {}) {
    const { ...query } = value;

    const queryParams = Object.keys(query)
      .filter((key) => query[key] != null && query[key] !== "")
      .map((key) => `${key}=${query[key]}`)
      .join("&");

    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/staking/operation/history?${queryParams}`,
          undefined,
          options,
        ),
      (d) => this.staking.setHistory(d, { append: value.offset > 0 }),
    );
  }
}
